import React, { useState, useEffect } from 'react';
import { Calendar, Flag, AlertCircle, CheckCircle, Clock, ChevronRight, Target, ChevronDown } from 'lucide-react';
import { api } from '../api/client';

const formatDate = (dateStr) => {
  if (!dateStr) return '-';
  const date = new Date(dateStr);
  return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
};

const daysUntil = (dateStr) => {
  if (!dateStr) return null;
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const target = new Date(dateStr);
  target.setHours(0, 0, 0, 0);
  const diff = Math.ceil((target - today) / (1000 * 60 * 60 * 24));
  return diff;
};

// Status colors mapping
const STATUS_COLORS = {
  'Completed': 'bg-green-50 border-green-200 text-green-800',
  'In Progress': 'bg-blue-50 border-blue-200 text-blue-800',
  'Not Started': 'bg-gray-50 border-gray-200 text-gray-600',
  'Behind': 'bg-red-50 border-red-200 text-red-800',
  'At Risk': 'bg-amber-50 border-amber-200 text-amber-800',
  'On Track': 'bg-green-50 border-green-200 text-green-800',
  'behind': 'bg-red-50 border-red-200 text-red-800',
  'at-risk': 'bg-amber-50 border-amber-200 text-amber-800',
  'on-track': 'bg-green-50 border-green-200 text-green-800',
  'default': 'bg-gray-50 border-gray-200 text-gray-600'
};

// Status indicator dot
const StatusDot = ({ status }) => {
  const colors = {
    'behind': 'bg-[var(--clr-sev-blocked)]',
    'at-risk': 'bg-[var(--clr-sev-atrisk)]',
    'on-track': 'bg-[var(--clr-sev-healthy)]'
  };
  return <span className={`w-2 h-2 rounded-full ${colors[status] || 'bg-gray-300'}`} />;
};

// Executive Milestones Widget - Modern clean design
export const ExecutiveMilestonesWidget = ({ portfolioId, programId, className = '' }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState(true);

  useEffect(() => {
    loadData();
  }, [portfolioId, programId]);

  const loadData = async () => {
    try {
      const params = {};
      if (portfolioId) params.portfolio_id = portfolioId;
      if (programId) params.program_id = programId;
      const res = await api.getExecutiveMilestones(params);
      setData(res.data?.data);
    } catch (error) {
      console.error('Failed to load executive milestones:', error);
    }
    setLoading(false);
  };

  if (loading) {
    return (
      <div className={`bg-[var(--clr-surface-card)] rounded-lg border border-[var(--clr-border-light)] shadow-[var(--shadow-card)] ${className}`}>
        <div className="p-4 border-b border-[var(--clr-border-light)]">
          <div className="animate-pulse h-6 bg-[var(--clr-surface-subtle)] rounded w-1/3"></div>
        </div>
        <div className="p-4">
          <div className="animate-pulse space-y-3">
            <div className="h-12 bg-[var(--clr-surface-subtle)] rounded"></div>
            <div className="h-12 bg-[var(--clr-surface-subtle)] rounded"></div>
          </div>
        </div>
      </div>
    );
  }

  const upcoming = data?.milestones?.filter(m => daysUntil(m.milestone_date) >= 0).slice(0, 5) || [];
  const overdue = data?.milestones?.filter(m => daysUntil(m.milestone_date) < 0).slice(0, 3) || [];
  const summary = data?.summary || {};

  return (
    <div className={`bg-[var(--clr-surface-card)] rounded-lg border border-[var(--clr-border-light)] shadow-[var(--shadow-card)] ${className}`} data-testid="executive-milestones-widget">
      {/* Header */}
      <button 
        onClick={() => setExpanded(!expanded)}
        className="w-full p-4 flex items-center justify-between hover:bg-[var(--clr-surface-hover)] transition-colors"
      >
        <div className="flex items-center gap-3">
          <Target className="w-5 h-5 text-[var(--clr-brand-primary)]" />
          <h3 className="text-base font-semibold text-[var(--clr-text-primary)]">Strategic Milestones</h3>
        </div>
        <div className="flex items-center gap-4">
          <div className="flex items-center gap-3 text-xs text-[var(--clr-text-muted)]">
            {summary.behind > 0 && (
              <span className="flex items-center gap-1.5">
                <StatusDot status="behind" />
                <span>{summary.behind} Behind</span>
              </span>
            )}
            {summary.at_risk > 0 && (
              <span className="flex items-center gap-1.5">
                <StatusDot status="at-risk" />
                <span>{summary.at_risk} At Risk</span>
              </span>
            )}
            {summary.on_track > 0 && (
              <span className="flex items-center gap-1.5">
                <StatusDot status="on-track" />
                <span>{summary.on_track} On Track</span>
              </span>
            )}
          </div>
          <ChevronDown className={`w-4 h-4 text-[var(--clr-text-muted)] transition-transform ${expanded ? 'rotate-180' : ''}`} />
        </div>
      </button>

      {expanded && (
        <div className="border-t border-[var(--clr-border-light)]">
          {/* Overdue Section */}
          {overdue.length > 0 && (
            <div className="p-4 border-b border-[var(--clr-border-light)] bg-[var(--clr-surface-subtle)]">
              <div className="flex items-center gap-2 mb-3">
                <AlertCircle className="w-4 h-4 text-[var(--clr-sev-blocked)]" />
                <span className="text-sm font-medium text-[var(--clr-text-primary)]">Overdue</span>
                <span className="text-xs text-[var(--clr-text-muted)]">({overdue.length})</span>
              </div>
              <div className="space-y-2">
                {overdue.map((m) => (
                  <div key={m.project_id} className="flex items-center justify-between py-2 px-3 bg-[var(--clr-surface-card)] rounded border border-[var(--clr-border-light)]">
                    <div className="flex items-center gap-2">
                      <StatusDot status="behind" />
                      <span className="text-sm font-medium text-[var(--clr-text-primary)]">{m.project_name}</span>
                    </div>
                    <span className="text-xs text-[var(--clr-text-muted)]">{formatDate(m.milestone_date)}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Upcoming Milestones */}
          <div className="p-4">
            {upcoming.length === 0 && overdue.length === 0 ? (
              <p className="text-sm text-[var(--clr-text-muted)] text-center py-4">No milestones with dates found</p>
            ) : (
              <div className="space-y-2">
                {upcoming.map((m) => {
                  const days = daysUntil(m.milestone_date);
                  return (
                    <div 
                      key={m.project_id} 
                      className="flex items-center justify-between py-3 px-3 rounded border border-[var(--clr-border-light)] hover:bg-[var(--clr-surface-hover)] transition-colors"
                    >
                      <div className="flex items-center gap-3">
                        <StatusDot status={m.status} />
                        <div>
                          <p className="text-sm font-medium text-[var(--clr-text-primary)]">{m.project_name}</p>
                          {m.program_name && (
                            <p className="text-xs text-[var(--clr-text-muted)]">{m.program_name}</p>
                          )}
                        </div>
                      </div>
                      <div className="text-right">
                        <p className="text-sm font-medium text-[var(--clr-text-primary)]">{formatDate(m.milestone_date)}</p>
                        <p className="text-xs text-[var(--clr-text-muted)]">
                          {days === 0 ? 'Today' : days === 1 ? 'Tomorrow' : `${days} days`}
                        </p>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

// PMO Milestones Widget - Shows Epic End Dates grouped by Project
export const PMOMilestonesWidget = ({ portfolioId, programId, projectId, className = '' }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [expandedProject, setExpandedProject] = useState(null);

  useEffect(() => {
    loadData();
  }, [portfolioId, programId, projectId]);

  const loadData = async () => {
    try {
      const params = {};
      if (portfolioId) params.portfolio_id = portfolioId;
      if (programId) params.program_id = programId;
      if (projectId) params.project_id = projectId;
      const res = await api.getPMOMilestones(params);
      setData(res.data?.data);
    } catch (error) {
      console.error('Failed to load PMO milestones:', error);
    }
    setLoading(false);
  };

  if (loading) {
    return (
      <div className={`bg-white rounded-xl border p-6 ${className}`}>
        <div className="animate-pulse space-y-4">
          <div className="h-6 bg-gray-100 rounded w-1/3"></div>
          <div className="h-32 bg-gray-100 rounded"></div>
        </div>
      </div>
    );
  }

  if (!data || !data.projects?.length) {
    return (
      <div className={`bg-white rounded-xl border p-6 ${className}`} data-testid="pmo-milestones-widget">
        <div className="flex items-center gap-2 mb-4">
          <Flag className="w-5 h-5 text-purple-600" />
          <h3 className="text-lg font-semibold">Epic Milestones</h3>
        </div>
        <p className="text-[var(--clr-text-muted)] text-sm">No epic milestones found</p>
      </div>
    );
  }

  return (
    <div className={`bg-white rounded-xl border p-6 ${className}`} data-testid="pmo-milestones-widget">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <Flag className="w-5 h-5 text-purple-600" />
          <h3 className="text-lg font-semibold">Epic Milestones</h3>
        </div>
        <span className="text-xs text-[var(--clr-text-muted)]">
          {data.total_epics} epics across {data.total_projects} projects
        </span>
      </div>

      <div className="flex items-center gap-4 mb-4 text-xs">
        <span className="flex items-center gap-1">
          <span className="w-2 h-2 rounded-full bg-red-500"></span>
          {data.summary?.behind || 0} Behind
        </span>
        <span className="flex items-center gap-1">
          <span className="w-2 h-2 rounded-full bg-yellow-500"></span>
          {data.summary?.at_risk || 0} At Risk
        </span>
        <span className="flex items-center gap-1">
          <span className="w-2 h-2 rounded-full bg-green-500"></span>
          {data.summary?.on_track || 0} On Track
        </span>
      </div>

      <div className="space-y-2">
        {data.projects.slice(0, 5).map((proj) => (
          <div key={proj.project_id} className="border rounded-lg">
            <button
              onClick={() => setExpandedProject(expandedProject === proj.project_id ? null : proj.project_id)}
              className="w-full p-3 flex items-center justify-between hover:bg-gray-50"
            >
              <div className="flex items-center gap-2">
                <ChevronRight className={`w-4 h-4 transition-transform ${expandedProject === proj.project_id ? 'rotate-90' : ''}`} />
                <span className="font-medium">{proj.project_name}</span>
                <span className="text-xs text-[var(--clr-text-muted)]">({proj.epic_count} epics)</span>
              </div>
              <div className="flex items-center gap-2">
                {proj.summary.behind > 0 && (
                  <span className="px-2 py-0.5 text-xs bg-red-100 text-red-700 rounded">{proj.summary.behind}</span>
                )}
                {proj.summary.at_risk > 0 && (
                  <span className="px-2 py-0.5 text-xs bg-yellow-100 text-yellow-700 rounded">{proj.summary.at_risk}</span>
                )}
                {proj.summary.on_track > 0 && (
                  <span className="px-2 py-0.5 text-xs bg-green-100 text-green-700 rounded">{proj.summary.on_track}</span>
                )}
              </div>
            </button>
            {expandedProject === proj.project_id && (
              <div className="px-3 pb-3 space-y-2">
                {proj.epics.slice(0, 5).map((epic) => (
                  <div 
                    key={epic.epic_id}
                    className={`p-2 rounded border text-sm ${STATUS_COLORS[epic.status] || STATUS_COLORS['default']}`}
                  >
                    <div className="flex items-center justify-between">
                      <span className="font-medium">{epic.epic_name}</span>
                      <span>{formatDate(epic.end_date)}</span>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
};

// PMO Feature Milestones Widget - Shows Feature End Dates grouped by Project
export const PMOFeatureMilestonesWidget = ({ portfolioId, programId, projectId, className = '' }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [expandedProject, setExpandedProject] = useState(null);

  useEffect(() => {
    loadData();
  }, [portfolioId, programId, projectId]);

  const loadData = async () => {
    try {
      const params = {};
      if (portfolioId) params.portfolio_id = portfolioId;
      if (programId) params.program_id = programId;
      if (projectId) params.project_id = projectId;
      const res = await api.getPMOFeatureMilestones(params);
      setData(res.data?.data);
    } catch (error) {
      console.error('Failed to load PMO feature milestones:', error);
    }
    setLoading(false);
  };

  if (loading) {
    return (
      <div className={`bg-white rounded-xl border p-6 ${className}`}>
        <div className="animate-pulse space-y-4">
          <div className="h-6 bg-gray-100 rounded w-1/3"></div>
          <div className="h-32 bg-gray-100 rounded"></div>
        </div>
      </div>
    );
  }

  if (!data || !data.projects?.length) {
    return (
      <div className={`bg-white rounded-xl border p-6 ${className}`} data-testid="pmo-feature-milestones-widget">
        <div className="flex items-center gap-2 mb-4">
          <Flag className="w-5 h-5 text-indigo-600" />
          <h3 className="text-lg font-semibold">Upcoming Milestones</h3>
        </div>
        <p className="text-[var(--clr-text-muted)] text-sm">No feature milestones found</p>
      </div>
    );
  }

  return (
    <div className={`bg-white rounded-xl border p-6 ${className}`} data-testid="pmo-feature-milestones-widget">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <Flag className="w-5 h-5 text-indigo-600" />
          <h3 className="text-lg font-semibold">Upcoming Milestones</h3>
        </div>
        <span className="text-xs text-[var(--clr-text-muted)]">
          {data.total_features} features across {data.total_projects} projects
        </span>
      </div>

      <div className="flex items-center gap-4 mb-4 text-xs">
        <span className="flex items-center gap-1">
          <span className="w-2 h-2 rounded-full bg-red-500"></span>
          {data.summary?.behind || 0} Behind
        </span>
        <span className="flex items-center gap-1">
          <span className="w-2 h-2 rounded-full bg-yellow-500"></span>
          {data.summary?.at_risk || 0} At Risk
        </span>
        <span className="flex items-center gap-1">
          <span className="w-2 h-2 rounded-full bg-green-500"></span>
          {data.summary?.on_track || 0} On Track
        </span>
      </div>

      <div className="space-y-2 max-h-[400px] overflow-y-auto">
        {data.projects.slice(0, 10).map((proj) => (
          <div key={proj.project_id} className="border rounded-lg">
            <button
              onClick={() => setExpandedProject(expandedProject === proj.project_id ? null : proj.project_id)}
              className="w-full p-3 flex items-center justify-between hover:bg-gray-50"
            >
              <div className="flex items-center gap-2">
                <ChevronRight className={`w-4 h-4 transition-transform ${expandedProject === proj.project_id ? 'rotate-90' : ''}`} />
                <span className="font-medium">{proj.project_name}</span>
                <span className="text-xs text-[var(--clr-text-muted)]">({proj.feature_count} features)</span>
              </div>
              <div className="flex items-center gap-2">
                {proj.summary.behind > 0 && (
                  <span className="px-2 py-0.5 text-xs bg-red-100 text-red-700 rounded">{proj.summary.behind}</span>
                )}
                {proj.summary.at_risk > 0 && (
                  <span className="px-2 py-0.5 text-xs bg-yellow-100 text-yellow-700 rounded">{proj.summary.at_risk}</span>
                )}
                {proj.summary.on_track > 0 && (
                  <span className="px-2 py-0.5 text-xs bg-green-100 text-green-700 rounded">{proj.summary.on_track}</span>
                )}
              </div>
            </button>
            {expandedProject === proj.project_id && (
              <div className="px-3 pb-3 space-y-2">
                {proj.features.slice(0, 10).map((feature) => (
                  <div 
                    key={feature.feature_id}
                    className={`p-2 rounded border text-sm ${STATUS_COLORS[feature.status] || STATUS_COLORS['default']}`}
                  >
                    <div className="flex items-center justify-between">
                      <div>
                        <span className="font-medium">{feature.feature_name}</span>
                        {feature.epic_name && (
                          <span className="text-xs ml-2 opacity-75">({feature.epic_name})</span>
                        )}
                      </div>
                      <span>{formatDate(feature.end_date)}</span>
                    </div>
                  </div>
                ))}
                {proj.features.length > 10 && (
                  <p className="text-xs text-center text-[var(--clr-text-muted)] py-2">
                    +{proj.features.length - 10} more features
                  </p>
                )}
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
};

// PM Milestones Widget - Shows Feature End Dates grouped by Epic
export const PMMilestonesWidget = ({ projectId, epicId, className = '' }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [expandedEpic, setExpandedEpic] = useState(null);

  useEffect(() => {
    loadData();
  }, [projectId, epicId]);

  const loadData = async () => {
    try {
      const params = {};
      if (projectId) params.project_id = projectId;
      if (epicId) params.epic_id = epicId;
      const res = await api.getPMMilestones(params);
      setData(res.data?.data);
    } catch (error) {
      console.error('Failed to load PM milestones:', error);
    }
    setLoading(false);
  };

  if (loading) {
    return (
      <div className={`bg-white rounded-xl border p-6 ${className}`}>
        <div className="animate-pulse space-y-4">
          <div className="h-6 bg-gray-100 rounded w-1/3"></div>
          <div className="h-32 bg-gray-100 rounded"></div>
        </div>
      </div>
    );
  }

  if (!data || !data.epics?.length) {
    return (
      <div className={`bg-white rounded-xl border p-6 ${className}`} data-testid="pm-milestones-widget">
        <div className="flex items-center gap-2 mb-4">
          <Calendar className="w-5 h-5 text-blue-600" />
          <h3 className="text-lg font-semibold">Feature Milestones</h3>
        </div>
        <p className="text-[var(--clr-text-muted)] text-sm">No feature milestones found</p>
      </div>
    );
  }

  return (
    <div className={`bg-white rounded-xl border p-6 ${className}`} data-testid="pm-milestones-widget">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <Calendar className="w-5 h-5 text-blue-600" />
          <h3 className="text-lg font-semibold">Feature Milestones</h3>
        </div>
        <span className="text-xs text-[var(--clr-text-muted)]">
          {data.total_features} features across {data.total_epics} epics
        </span>
      </div>

      <div className="flex items-center gap-4 mb-4 text-xs">
        <span className="flex items-center gap-1">
          <span className="w-2 h-2 rounded-full bg-red-500"></span>
          {data.summary?.behind || 0} Behind
        </span>
        <span className="flex items-center gap-1">
          <span className="w-2 h-2 rounded-full bg-yellow-500"></span>
          {data.summary?.at_risk || 0} At Risk
        </span>
        <span className="flex items-center gap-1">
          <span className="w-2 h-2 rounded-full bg-green-500"></span>
          {data.summary?.on_track || 0} On Track
        </span>
      </div>

      <div className="space-y-2">
        {data.epics.slice(0, 5).map((epic) => (
          <div key={epic.epic_id} className="border rounded-lg">
            <button
              onClick={() => setExpandedEpic(expandedEpic === epic.epic_id ? null : epic.epic_id)}
              className="w-full p-3 flex items-center justify-between hover:bg-gray-50"
            >
              <div className="flex items-center gap-2">
                <ChevronRight className={`w-4 h-4 transition-transform ${expandedEpic === epic.epic_id ? 'rotate-90' : ''}`} />
                <span className="font-medium">{epic.epic_name}</span>
                <span className="text-xs text-[var(--clr-text-muted)]">({epic.feature_count} features)</span>
              </div>
              <div className="flex items-center gap-2">
                {epic.summary.behind > 0 && (
                  <span className="px-2 py-0.5 text-xs bg-red-100 text-red-700 rounded">{epic.summary.behind}</span>
                )}
                {epic.summary.at_risk > 0 && (
                  <span className="px-2 py-0.5 text-xs bg-yellow-100 text-yellow-700 rounded">{epic.summary.at_risk}</span>
                )}
                {epic.summary.on_track > 0 && (
                  <span className="px-2 py-0.5 text-xs bg-green-100 text-green-700 rounded">{epic.summary.on_track}</span>
                )}
              </div>
            </button>
            {expandedEpic === epic.epic_id && (
              <div className="px-3 pb-3 space-y-2">
                {epic.features.slice(0, 5).map((feature) => (
                  <div 
                    key={feature.feature_id}
                    className={`p-2 rounded border text-sm ${STATUS_COLORS[feature.status] || STATUS_COLORS['default']}`}
                  >
                    <div className="flex items-center justify-between">
                      <span className="font-medium">{feature.feature_name}</span>
                      <span>{formatDate(feature.end_date)}</span>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
};

// Compact Milestone Summary for inline display
export const MilestonesSummary = ({ level = 'executive', portfolioId, programId, projectId, className = '' }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadData();
  }, [level, portfolioId, programId, projectId]);

  const loadData = async () => {
    try {
      const params = {};
      if (portfolioId) params.portfolio_id = portfolioId;
      if (programId) params.program_id = programId;
      if (projectId) params.project_id = projectId;

      let res;
      if (level === 'executive') {
        res = await api.getExecutiveMilestones(params);
      } else if (level === 'pmo') {
        res = await api.getPMOMilestones(params);
      } else {
        res = await api.getPMMilestones(params);
      }
      setData(res.data?.data);
    } catch (error) {
      console.error('Failed to load milestones summary:', error);
    }
    setLoading(false);
  };

  if (loading || !data) return null;

  const summary = data.summary || {};
  const total = (summary.behind || 0) + (summary.at_risk || 0) + (summary.on_track || 0);

  if (total === 0) return null;

  return (
    <div className={`flex items-center gap-3 ${className}`} data-testid="milestones-summary">
      <Calendar className="w-4 h-4 text-[var(--clr-text-muted)]" />
      <span className="text-sm text-[var(--clr-text-muted)]">{total} milestones:</span>
      {summary.behind > 0 && (
        <span className="text-xs px-2 py-0.5 bg-red-100 text-red-700 rounded-full">
          {summary.behind} behind
        </span>
      )}
      {summary.at_risk > 0 && (
        <span className="text-xs px-2 py-0.5 bg-yellow-100 text-yellow-700 rounded-full">
          {summary.at_risk} at risk
        </span>
      )}
      {summary.on_track > 0 && (
        <span className="text-xs px-2 py-0.5 bg-green-100 text-green-700 rounded-full">
          {summary.on_track} on track
        </span>
      )}
    </div>
  );
};

export default ExecutiveMilestonesWidget;
